/**
 * The Ledger IconButton. Import icon buttons from here, never from
 * `@chakra-ui/react` (a Biome rule enforces it).
 *
 * A Button with only an icon: square at 24, 28 or 32 (the `button` recipe's
 * sizes), an `aria-label` it cannot be rendered without, and a 32px press area
 * drawn by an invisible centred box, so a 24px icon is still easy to hit.
 * `tooltip` shows the label (or the node given) after 140ms; `busy` and
 * `disabledReason` behave as on Button.
 *
 * Spec: specs/design-system/ledger-primitives.feature
 */
import type { SystemStyleObject } from "@chakra-ui/react";
import * as React from "react";
import { Button, type ButtonProps } from "./button";
import { Tooltip } from "./tooltip";

export interface IconButtonProps extends ButtonProps {
  /** What the button does. Required: the icon alone names nothing. */
  "aria-label": string;
  /**
   * Show a tooltip: `true` repeats the `aria-label`, a node replaces it. Off by
   * default because most existing icon buttons already sit inside a Tooltip
   * of their own.
   */
  tooltip?: boolean | React.ReactNode;
}

/** The invisible 32px press area around a small control. */
export const HIT_AREA = {
  content: '""',
  position: "absolute",
  top: "50%",
  left: "50%",
  width: "max(100%, var(--hit-min))",
  height: "max(100%, var(--hit-min))",
  transform: "translate(-50%, -50%)",
} as const;

/**
 * The 32px press area below 1024 for a shell control drawn smaller than 32px
 * (a 28px sidebar entry, a switcher, the avatar, the logo): the same centred
 * box, and the control stands clear of the ones stacked above and below it by
 * the difference, so no two press areas overlap. At 1024 and up the controls
 * take presses at their drawn size.
 */
export const COMPACT_PRESS_AREA = {
  position: "relative",
  marginY: {
    base: "calc((var(--hit-min) - var(--control-md)) / 2)",
    lg: "0",
  },
  _after: { ...HIT_AREA, display: { base: "block", lg: "none" } },
} satisfies SystemStyleObject;

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton({ tooltip, ...props }, ref) {
    const button = (
      <Button ref={ref} px="0" py="0" _after={HIT_AREA} {...props} />
    );
    if (!tooltip || props.disabledReason != null) return button;
    return (
      <Tooltip
        content={tooltip === true ? props["aria-label"] : tooltip}
        openDelay={140}
      >
        {button}
      </Tooltip>
    );
  },
);
